/**
 * Focus trap utility for modals/dialogs.
 *
 * install(container) captura el foco adentro del container: Tab/Shift+Tab
 * ciclan entre los elementos focusables. Tambien guarda el element que tenia
 * foco antes del install y lo restaura al uninstall.
 *
 * Uso:
 *   const trap = install(modalEl);
 *   // ... cuando cerras el modal:
 *   trap.uninstall();
 */

export interface FocusTrap {
  /** Libera el foco y restaura al elemento previo. */
  uninstall(): void;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'ext-button:not([disabled])',
  'ext-input:not([disabled])',
  'ext-textarea:not([disabled])',
  'ext-select:not([disabled])',
  'ext-autocomplete:not([disabled])',
].join(',');

function getFocusable(container: HTMLElement): HTMLElement[] {
  const nodes = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  return nodes.filter((el) => {
    if (el.hasAttribute('disabled')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    // visible?
    if (el.offsetParent === null && el !== document.activeElement) return false;
    return true;
  });
}

export function install(container: HTMLElement): FocusTrap {
  const previousActive = (document.activeElement as HTMLElement | null) ?? null;

  const onKeyDown = (ev: KeyboardEvent): void => {
    if (ev.key !== 'Tab') return;
    const focusables = getFocusable(container);
    if (focusables.length === 0) {
      ev.preventDefault();
      container.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement as HTMLElement | null;

    if (ev.shiftKey) {
      if (active === first || !container.contains(active)) {
        ev.preventDefault();
        last.focus();
      }
    } else {
      if (active === last || !container.contains(active)) {
        ev.preventDefault();
        first.focus();
      }
    }
  };

  container.addEventListener('keydown', onKeyDown, true);

  // Foco inicial al primer focusable, o al container mismo como fallback.
  requestAnimationFrame(() => {
    const focusables = getFocusable(container);
    if (focusables.length > 0) {
      focusables[0].focus();
    } else {
      if (!container.hasAttribute('tabindex')) {
        container.setAttribute('tabindex', '-1');
      }
      container.focus();
    }
  });

  return {
    uninstall(): void {
      container.removeEventListener('keydown', onKeyDown, true);
      if (previousActive && typeof previousActive.focus === 'function') {
        try {
          previousActive.focus();
        } catch {
          // noop: el element puede haberse desmontado
        }
      }
    },
  };
}
